/* Shared math for the "Season Comparison" feature — used by SeasonForm's
   Historical Monthly tab (manual capture for Closed seasons) and the
   live monthly aggregator for Open seasons + the comparison module
   itself. Kept in its own file (not duplicated per-file) since it's pure
   calculation with no UI, unlike the registry Field/Section/Badge
   components CLAUDE.md says to keep duplicated.

   OPERATING CYCLE: every season's 12 rows are always August (start
   year) → July (start year + 1), regardless of when real activity
   actually happened — SEASON_MONTH_ORDER below is the single source of
   truth for that order. This DOES cross the Dec→Jan calendar boundary
   by design (Aug-Dec use the season's start year, Jan-Jul use start
   year + 1) — see yearForCycleMonth/seasonCycleStartYear. A month with
   no real activity for that season is just left blank/zero by the
   user; the row itself never disappears or reorders. (Supersedes an
   earlier, narrower assumption that seasons never cross a year
   boundary — that held for *where data lands* but not for the
   canonical row order/labeling the user wants.) */

import { resolveSeasonId, isSeasonOpen } from "./seasonFilter";

function loadLS(k, fb) { try { var d = localStorage.getItem(k); return d ? JSON.parse(d) : fb; } catch (e) { return fb; } }

function pad2(n) { return n < 10 ? "0" + n : String(n); }
function toISO(d) { return d.getFullYear() + "-" + pad2(d.getMonth() + 1) + "-" + pad2(d.getDate()); }

/* nth weekday of a month (0-based month, weekday 0=Sun..6=Sat, n=1..5). */
function nthWeekdayOfMonth(year, month, weekday, n) {
  var first = new Date(year, month, 1);
  var offset = (weekday - first.getDay() + 7) % 7;
  return new Date(year, month, 1 + offset + (n - 1) * 7);
}
function lastWeekdayOfMonth(year, month, weekday) {
  var last = new Date(year, month + 1, 0);
  var offset = (last.getDay() - weekday + 7) % 7;
  return new Date(year, month, last.getDate() - offset);
}
/* Federal "observed" rule: Saturday → observed Friday before, Sunday →
   observed Monday after. No-op for the floating (always-Monday/Thursday)
   holidays — applying it uniformly is harmless and keeps this simple. */
function observed(date) {
  var dow = date.getDay();
  if (dow === 6) return new Date(date.getFullYear(), date.getMonth(), date.getDate() - 1);
  if (dow === 0) return new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1);
  return date;
}

/* The 11 US federal holidays for a given year, "observed" dates. */
export function usFederalHolidays(year) {
  return [
    new Date(year, 0, 1),                  // New Year's Day
    nthWeekdayOfMonth(year, 0, 1, 3),       // MLK Day — 3rd Mon Jan
    nthWeekdayOfMonth(year, 1, 1, 3),       // Presidents Day — 3rd Mon Feb
    lastWeekdayOfMonth(year, 4, 1),         // Memorial Day — last Mon May
    new Date(year, 5, 19),                  // Juneteenth
    new Date(year, 6, 4),                   // Independence Day
    nthWeekdayOfMonth(year, 8, 1, 1),       // Labor Day — 1st Mon Sep
    nthWeekdayOfMonth(year, 9, 1, 2),       // Columbus Day — 2nd Mon Oct
    new Date(year, 10, 11),                 // Veterans Day
    nthWeekdayOfMonth(year, 10, 4, 4),      // Thanksgiving — 4th Thu Nov
    new Date(year, 11, 25),                 // Christmas
  ].map(observed);
}

var holidaySetCache = {};
function holidaySetForYear(year) {
  if (!holidaySetCache[year]) {
    holidaySetCache[year] = new Set(usFederalHolidays(year).map(toISO));
  }
  return holidaySetCache[year];
}

/* Inclusive business-day count between two ISO ("YYYY-MM-DD") dates,
   excluding weekends and US federal holidays. */
export function countBusinessDays(startISO, endISO) {
  if (!startISO || !endISO) return 0;
  var start = new Date(startISO + "T00:00:00");
  var end = new Date(endISO + "T00:00:00");
  if (end < start) return 0;
  var count = 0;
  var cur = new Date(start);
  while (cur <= end) {
    var dow = cur.getDay();
    if (dow !== 0 && dow !== 6 && !holidaySetForYear(cur.getFullYear()).has(toISO(cur))) count++;
    cur.setDate(cur.getDate() + 1);
  }
  return count;
}

/* Business days for ONE calendar month of a season, clipped to the
   season's actual operative range. Handles all 3 cases from the spec
   via simple clipping — no separate first/middle/last branches needed:
   - First month: range start clips monthStart forward.
   - Last month: range end clips monthEnd backward.
   - Middle months: range doesn't reach into the month, no clipping. */
export function businessDaysForMonth(year, month, seasonRange) {
  var monthStart = new Date(year, month - 1, 1);
  var monthEnd = new Date(year, month, 0);
  var rangeStart = seasonRange && seasonRange.firstDate ? new Date(seasonRange.firstDate + "T00:00:00") : null;
  var rangeEnd = seasonRange && seasonRange.lastDate ? new Date(seasonRange.lastDate + "T00:00:00") : null;
  var effStart = rangeStart && rangeStart > monthStart ? rangeStart : monthStart;
  var effEnd = rangeEnd && rangeEnd < monthEnd ? rangeEnd : monthEnd;
  if (effEnd < effStart) return 0;
  return countBusinessDays(toISO(effStart), toISO(effEnd));
}

/* Season's operative date range — real shipments first (Open seasons),
   falling back to season.startDate / closeDate-or-endEstimate (Closed
   seasons with no linked shipments), per spec point 4. `shipments`
   should already be filtered to just this season by the caller.

   When shipments ARE present, firstDate/lastDate come from MIN/MAX
   shipment.dateArrival — NOT season.startDate/endEstimate — so a
   season that starts on the 25th but whose first shipment doesn't
   arrive until the 27th correctly starts its first-month business-day
   count on the 27th (confirmed by test: "clips first month to the
   first real arrival, not season.startDate").

   closeDate is preferred over endEstimate in the fallback because it's
   the REAL close date once a season is actually closed; endEstimate is
   only ever a target/estimate set when the season was still open. */
export function resolveSeasonRange(season, shipments) {
  var dates = (shipments || []).map(function (s) { return s.dateArrival; }).filter(Boolean).sort();
  if (dates.length > 0) return { firstDate: dates[0], lastDate: dates[dates.length - 1] };
  return { firstDate: season.startDate || null, lastDate: (season.closeDate || season.endEstimate) || null };
}

/* Canonical row order for every season, everywhere: August (start
   year) through July (start year + 1). Single source of truth — import
   this instead of re-deriving the order anywhere else. */
export var SEASON_MONTH_ORDER = [8, 9, 10, 11, 12, 1, 2, 3, 4, 5, 6, 7];

/* Calendar year for a given month within the Aug→Jul cycle, anchored
   to the season's start year. Months 8-12 fall in the start year
   itself; months 1-7 fall in the following calendar year. */
export function yearForCycleMonth(month, startYear) {
  return month >= 8 ? startYear : startYear + 1;
}

/* The season's start year — the anchor SEASON_MONTH_ORDER's Aug row
   uses. Prefers season.startDate; falls back to a 4-digit year parsed
   out of seasonName (e.g. "2025-2026" -> 2025) for the rare record
   with a name but no date yet; falls back to the current year so a
   brand-new, still-blank season doesn't crash the Historical Monthly
   tab before the user has picked a start date. */
export function seasonCycleStartYear(season) {
  if (season && season.startDate) return new Date(season.startDate + "T00:00:00").getFullYear();
  if (season && season.seasonName) {
    var m = String(season.seasonName).match(/(\d{4})/);
    if (m) return parseInt(m[1], 10);
  }
  return new Date().getFullYear();
}

/* Fills in the 4 live-derived columns (Acumulado, Profit/box,
   Profit/day, Profit/hour) for a season's 12 month rows. `monthRows` is
   the captured/aggregated base data — [{month:1..12, cases, profit}],
   gaps allowed for months with no activity; input order doesn't
   matter, rows are looked up by month number. Returns all 12 months in
   SEASON_MONTH_ORDER (Aug→Jul) — NOT calendar 1→12 — each carrying its
   own resolved `year` (for "Aug 2025" style labels) and with the
   derived fields set to null when that month has no data (blank cell,
   not zero). Acumulado's running sum follows this SAME Aug→Jul order,
   so August is always where the running total starts, regardless of
   the season's real activity dates. */
export function computeDerivedMonthly(monthRows, startYear, seasonRange) {
  var byMonth = {};
  (monthRows || []).forEach(function (r) { if (r && r.month) byMonth[r.month] = r; });
  var running = 0;
  var out = [];
  SEASON_MONTH_ORDER.forEach(function (m) {
    var year = yearForCycleMonth(m, startYear);
    var row = byMonth[m];
    var cases = row && row.cases !== "" && row.cases != null ? Number(row.cases) : null;
    var profit = row && row.profit !== "" && row.profit != null ? Number(row.profit) : null;
    var hasData = cases !== null || profit !== null;
    var acumulado = null, profitBox = null, profitDay = null, profitHour = null, businessDays = null;
    if (hasData) {
      running += (profit || 0);
      acumulado = running;
      profitBox = cases ? (profit || 0) / cases : 0;
      businessDays = businessDaysForMonth(year, m, seasonRange);
      profitDay = businessDays > 0 ? (profit || 0) / businessDays : 0;
      profitHour = profitDay / 8;
    }
    out.push({ month: m, year: year, cases: cases, profit: profit, acumulado: acumulado, profitBox: profitBox, profitDay: profitDay, profitHour: profitHour, businessDays: businessDays });
  });
  return out;
}

/* Real per-invoice case rows for one lot — mirrors SeasonRegistry.js's
   calcSeasonProfits() / ProfitTracker.js's invoiceRowsForLot verbatim
   (same intentional "no shared internals" duplication as those two —
   see SeasonRegistry.js's own comment on calcSeasonProfits). Fixes the
   split-lot profit duplication: when a lot is divided across 2+
   invoices, each invoice's lines[] carries a full copy of the
   shipment's product lines (the lot's TOTAL cases), while
   invoice.palletAllocations is what actually gets edited to reflect
   the real per-invoice split. */
function invoiceRowsForLotLocal(inv, lot, shipByLot) {
  var allocs = (inv.palletAllocations || []).filter(function (a) { return a && String(a.lot) === String(lot) && (parseInt(a.cases, 10) || 0) > 0; });
  if (allocs.length > 0) {
    var shp = shipByLot[lot];
    var manifest = shp ? (shp.palletManifest || []) : [];
    var lines = inv.lines || [];
    var out = [];
    allocs.forEach(function (a) {
      var meta = manifest.find(function (p) { return String(p.palletNum) === String(a.palletNum); }) || null;
      var line = meta && lines.find(function (l) {
        return (l.productBase || l.productName || "") === (meta.productBase || "")
          && (l.category || "") === (meta.category || "")
          && (l.size || "") === (meta.size || "");
      });
      if (!line && meta) {
        line = lines.find(function (l) {
          return (l.productBase || l.productName || "") === (meta.productBase || "")
            && (l.category || "") === (meta.category || "");
        });
      }
      var saleStatus = line ? line.saleStatus : "";
      if (saleStatus === "dumped" || saleStatus === "donated" || saleStatus === "samples" || saleStatus === "transfer") return;
      out.push({
        product: (line && (line.productName || line.productBase)) || (meta && meta.productBase) || "?",
        cases: parseInt(a.cases, 10) || 0,
        salePrice: line ? (parseFloat(line.salePrice) || 0) : 0,
        commissionRate: line ? line.commissionRate : undefined,
      });
    });
    return out;
  }
  return (inv.lines || []).filter(function (l) {
    if (l.lot && l.lot !== lot) return false;
    return l.saleStatus !== "dumped" && l.saleStatus !== "donated" && l.saleStatus !== "samples" && l.saleStatus !== "transfer";
  }).map(function (l) {
    return { product: l.productName || l.productBase || "?", cases: parseInt(l.cases, 10) || 0, salePrice: parseFloat(l.salePrice) || 0, commissionRate: l.commissionRate };
  });
}

/* Live monthly aggregation for an Open (or preClose/reOpened) season.
   Same per-lot profit math as calcSeasonProfits (priceDiff net of
   client commission, shipper commission, fee markup, finance markup)
   but bucketed by CALENDAR MONTH instead of ISO week, and keyed
   strictly by shipment.dateArrival — NO dateDeparture fallback, per
   spec. A lot whose shipment has no dateArrival is excluded from every
   month bucket (it has nowhere correct to go), even though it may
   still belong to this season by other means. */
export function aggregateOpenSeasonMonthly(season) {
  var allSeasons = loadLS("gfi-seasons", []);
  var bills = loadLS("gfi-bills", []);
  var invoices = loadLS("gfi-invoices", []);
  var shipments = loadLS("gfi-shipments", []);
  var expenseBills = loadLS("gfi-expense-bills", []);

  var shipByLot = {};
  shipments.forEach(function (s) { if (s.lot) shipByLot[s.lot] = s; });

  var seasonId = String(season.id);
  var lotsInSeason = new Set();
  shipments.forEach(function (s) {
    if (!s.lot) return;
    var sid = resolveSeasonId(allSeasons, s.dateArrival, s.seasonId);
    if (sid === seasonId) lotsInSeason.add(String(s.lot));
  });

  var expenseByLot = {};
  expenseBills.forEach(function (eb) {
    var fees = eb.fees || [];
    var dist = eb.distribution || [];
    var linkedLots = eb.linkedLots || [];
    if (fees.length > 0) {
      fees.forEach(function (fe) {
        if (!fe.shipmentId) return;
        var shp = shipments.find(function (s) { return s.id === fe.shipmentId; });
        var lot = shp ? shp.lot : null;
        if (!lot || !lotsInSeason.has(String(lot))) return;
        if (!expenseByLot[lot]) expenseByLot[lot] = [];
        expenseByLot[lot].push({ actualCost: parseFloat(fe.amount) || 0 });
      });
      var unlinked = fees.filter(function (fe) { return !fe.shipmentId && parseFloat(fe.amount) > 0; });
      if (unlinked.length > 0 && linkedLots.length > 0) {
        unlinked.forEach(function (fe) {
          var perLot = (parseFloat(fe.amount) || 0) / linkedLots.length;
          linkedLots.forEach(function (sid2) { var shp = shipments.find(function (s) { return s.id === sid2; }); if (shp && shp.lot && lotsInSeason.has(String(shp.lot))) { if (!expenseByLot[shp.lot]) expenseByLot[shp.lot] = []; expenseByLot[shp.lot].push({ actualCost: perLot }); } });
        });
      }
    } else if (dist.length > 0) {
      dist.forEach(function (d) { if (d.lot && lotsInSeason.has(String(d.lot))) { if (!expenseByLot[d.lot]) expenseByLot[d.lot] = []; var pallets = parseInt(d.pallets, 10) || 0; var cost = d.costRate ? (parseFloat(d.costRate) || 0) * pallets : (parseFloat(d.amount) || 0); expenseByLot[d.lot].push({ actualCost: cost }); } });
    } else if (linkedLots.length > 0) {
      var perLot2 = (parseFloat(eb.amount) || 0) / linkedLots.length;
      linkedLots.forEach(function (sid3) { var shp = shipments.find(function (s) { return s.id === sid3; }); if (shp && shp.lot && lotsInSeason.has(String(shp.lot))) { if (!expenseByLot[shp.lot]) expenseByLot[shp.lot] = []; expenseByLot[shp.lot].push({ actualCost: perLot2 }); } });
    }
  });

  var lotMap = {};
  bills.forEach(function (b) { if (b.lot && lotsInSeason.has(String(b.lot))) { if (!lotMap[b.lot]) lotMap[b.lot] = { bills: [], invoices: [] }; lotMap[b.lot].bills.push(b); } });
  invoices.forEach(function (inv) {
    var lots = new Set();
    (inv.lines || []).forEach(function (l) { if (l.lot) lots.add(l.lot); });
    if (lots.size === 0 && inv.lot) lots.add(inv.lot);
    lots.forEach(function (lot) { if (lotsInSeason.has(String(lot))) { if (!lotMap[lot]) lotMap[lot] = { bills: [], invoices: [] }; lotMap[lot].invoices.push(inv); } });
  });

  var monthly = {};
  Object.keys(lotMap).forEach(function (lot) {
    var entry = { lot: lot, bills: lotMap[lot].bills, invoices: lotMap[lot].invoices };
    var shp = shipByLot[lot];
    if (!shp || !shp.dateArrival) return;
    var month = new Date(shp.dateArrival + "T00:00:00").getMonth() + 1;

    var repByProd = {}; var saleByProd = {};
    entry.bills.forEach(function (b) { (b.lines || []).forEach(function (l) { var k = l.productName || l.productBase || "?"; var c = parseInt(l.cases, 10) || 0; if (!repByProd[k]) repByProd[k] = 0; repByProd[k] += c * (parseFloat(l.reportedPrice) || 0); }); });
    entry.invoices.forEach(function (inv) {
      var invDefRate = parseFloat(inv.commissionRate) || 0;
      invoiceRowsForLotLocal(inv, entry.lot, shipByLot).forEach(function (row) {
        var k = row.product;
        var lineRate = (row.commissionRate === undefined || row.commissionRate === null || row.commissionRate === "")
          ? invDefRate
          : (parseFloat(row.commissionRate) || 0);
        var netSp = row.salePrice * (1 - lineRate / 100);
        if (!saleByProd[k]) saleByProd[k] = 0;
        saleByProd[k] += row.cases * netSp;
      });
    });
    var lotPD = 0;
    new Set([].concat(Object.keys(repByProd), Object.keys(saleByProd))).forEach(function (k) { lotPD += (saleByProd[k] || 0) - (repByProd[k] || 0); });

    var lotComm = 0;
    entry.bills.forEach(function (b) {
      var billDefRate = parseFloat(b.commissionRate) || 0;
      (b.lines || []).forEach(function (l) {
        if (l.saleStatus === "dumped" || l.saleStatus === "donated" || l.saleStatus === "samples" || l.saleStatus === "transfer") return;
        var lineAmt = (parseInt(l.cases, 10) || 0) * (parseFloat(l.reportedPrice) || 0);
        var lineRate = (l.commissionRate === undefined || l.commissionRate === null || l.commissionRate === "")
          ? billDefRate
          : (parseFloat(l.commissionRate) || 0);
        lotComm += lineAmt * (lineRate / 100);
      });
    });

    var billFees = 0; var financeCharged = 0;
    entry.bills.forEach(function (b) { (b.fees || []).forEach(function (fe) { if (fe.passThrough) return; var amt = parseFloat(fe.amount) || 0; var fname = fe.feeType === "__custom__" ? fe.feeCustom : (fe.feeType || "Other"); if (fname === "Advance Financing Fee") { financeCharged += amt; } else { billFees += amt; } }); });
    var actualCosts = 0;
    (expenseByLot[lot] || []).forEach(function (exp) { actualCosts += exp.actualCost; });
    var lotFM = billFees - actualCosts;

    var financeCost = 0;
    entry.invoices.forEach(function (inv) {
      (inv.fees || []).forEach(function (fe) {
        var fname = fe.feeType === "__custom__" ? fe.feeCustom : (fe.feeType || "");
        if (fname !== "Advance Financing Fee") return;
        var amt = parseFloat(fe.amount) || 0;
        if (amt === 0) return;
        if (fe.transferTargetBillId) {
          var tb = bills.find(function (b) { return b.id === fe.transferTargetBillId; });
          if (tb && tb.lot === entry.lot) financeCost += amt;
          else if (tb && tb.lot !== entry.lot) return;
          else financeCost += amt;
        } else if (!inv.lot || inv.lot === entry.lot) {
          var invLots = new Set(); (inv.lines || []).forEach(function (l) { if (l.lot) invLots.add(l.lot); });
          if (invLots.size <= 1) financeCost += amt;
          else {
            var tC = 0, lC = 0;
            (inv.lines || []).forEach(function (l) { var cs = parseInt(l.cases, 10) || 0; tC += cs; if (!l.lot || l.lot === entry.lot) lC += cs; });
            financeCost += amt * (tC > 0 ? lC / tC : 0);
          }
        }
      });
    });
    var lotFinance = financeCharged - financeCost;

    var lotProfit = lotPD + lotComm + lotFM + lotFinance;
    var lotCases = 0;
    entry.invoices.forEach(function (inv) { invoiceRowsForLotLocal(inv, entry.lot, shipByLot).forEach(function (row) { lotCases += row.cases; }); });
    if (lotCases === 0) { entry.bills.forEach(function (b) { (b.lines || []).forEach(function (l) { lotCases += parseInt(l.cases, 10) || 0; }); }); }

    if (!monthly[month]) monthly[month] = { month: month, cases: 0, profit: 0, lots: [] };
    monthly[month].cases += lotCases;
    monthly[month].profit += lotProfit;
    monthly[month].lots.push(lot);
  });

  var seasonShipments = shipments.filter(function (s) { return s.lot && lotsInSeason.has(String(s.lot)); });
  var seasonRange = resolveSeasonRange(season, seasonShipments);
  return { monthRows: Object.values(monthly), seasonRange: seasonRange };
}

/* Single entry point Phase 3's comparison module calls regardless of
   season status: Closed reads the manually-captured
   season.monthlyHistorical, everything operationally "open" (open /
   preClose / reOpened — see seasonFilter.isSeasonOpen) computes live
   from real records. Both paths funnel through computeDerivedMonthly
   so the 4 derived columns are always calculated the same way. */
export function computeSeasonMonthlyComparison(season) {
  var isOpen = isSeasonOpen(season.status);
  var monthRows, seasonRange;
  if (isOpen) {
    var agg = aggregateOpenSeasonMonthly(season);
    monthRows = agg.monthRows;
    seasonRange = agg.seasonRange;
  } else {
    monthRows = season.monthlyHistorical || [];
    seasonRange = resolveSeasonRange(season, []);
  }
  var startYear = seasonCycleStartYear(season);
  var base = SEASON_MONTH_ORDER.map(function (m) {
    var found = monthRows.find(function (r) { return r && r.month === m; });
    return found || { month: m, cases: "", profit: "" };
  });
  return { rows: computeDerivedMonthly(base, startYear, seasonRange), seasonRange: seasonRange, source: isOpen ? "live" : "historical" };
}

/* 0-based position of a calendar date within the Aug->Jul cycle
   (0=Aug ... 11=Jul) — independent of any specific season's start
   year, since the cycle's calendar-month mapping never changes.
   Used to answer "how far into the cycle are we today", for comparing
   the active season against Closed seasons "at the same point". */
export function cyclePositionForDate(date) {
  var month = date.getMonth() + 1;
  return SEASON_MONTH_ORDER.indexOf(month);
}

/* Year-to-date totals through a given cycle position, for a season's
   rows (already in SEASON_MONTH_ORDER from computeSeasonMonthlyComparison).
   `cases` is the sum of every month's cases from the start of the
   cycle through `position` (months with no data contribute 0, but
   don't block the sum). `profit` is the running Acumulado value as of
   that position — since Acumulado already IS a cumulative sum, the
   last non-null one at or before `position` IS the YTD profit total.

   PRECISION NOTE: the data model is monthly, not daily — there is no
   day-level cumulative figure to compare against. "Same point in
   time" is therefore "same position in the Aug->Jul cycle" (i.e. same
   calendar month), the finest granularity the captured/aggregated
   data actually supports. */
export function ytdThroughPosition(rows, position) {
  var cases = 0;
  var profit = null;
  var hasAny = false;
  for (var i = 0; i <= position && i < rows.length; i++) {
    var r = rows[i];
    if (!r) continue;
    if (r.cases != null) { cases += r.cases; hasAny = true; }
    if (r.acumulado != null) { profit = r.acumulado; hasAny = true; }
  }
  return { cases: hasAny ? cases : null, profit: profit };
}

/* Plain SUM(profit)/SUM(cases) over a Closed season's manually-captured
   monthlyHistorical rows — no business-day math, no Aug->Jul reordering,
   just the raw totals. Used by SeasonRegistry.js's list/detail "Profit
   Real" and "Profit/box" columns, which previously only ever summed
   from live bills/invoices (calcSeasonProfits) — a season captured
   entirely through Historical Monthly (no linked shipments) had nothing
   for that path to find, so it always showed 0. Returns null (not a
   zero-filled object) when the season has no real captured data at
   all, so callers can cleanly fall back to their existing live-data
   path instead of overwriting it with a false zero. */
export function historicalSeasonTotals(season) {
  var rows = Array.isArray(season && season.monthlyHistorical) ? season.monthlyHistorical : [];
  var hasData = rows.some(function (r) { return r && ((r.cases !== "" && r.cases != null) || (r.profit !== "" && r.profit != null)); });
  if (!hasData) return null;
  var totalCases = 0, totalProfit = 0;
  rows.forEach(function (r) {
    if (!r) return;
    if (r.cases !== "" && r.cases != null) totalCases += Number(r.cases) || 0;
    if (r.profit !== "" && r.profit != null) totalProfit += Number(r.profit) || 0;
  });
  return { profit: totalProfit, cases: totalCases, profitBox: totalCases > 0 ? totalProfit / totalCases : 0 };
}
